import React, { useState, useEffect } from 'react';
import {
  ScrollText,
  Search,
  Download,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Info,
  ChevronLeft,
  ChevronRight,
  Loader2
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { eventLogsAPI } from '../services/api';

const EventLog = () => {
  const [eventLogs, setEventLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, info: 0, warning: 0, error: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchData();
  }, [currentPage, levelFilter, sourceFilter, searchQuery]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: itemsPerPage
      };
      if (levelFilter !== 'all') params.level = levelFilter;
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (searchQuery) params.search = searchQuery;

      const [logsRes, statsRes] = await Promise.all([
        eventLogsAPI.list(params),
        eventLogsAPI.getStats()
      ]);
      
      setEventLogs(logsRes.data.data || []);
      setTotalPages(logsRes.data.pages || 1);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching event logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLevelIcon = (level) => {
    switch (level) {
      case 'info':
        return <Info className="w-4 h-4 text-blue-400" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-yellow-400" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-400" />;
      default:
        return <CheckCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getLevelBadge = (level) => {
    const colors = {
      info: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      warning: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      error: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return <Badge className={colors[level] || 'bg-gray-500/20 text-gray-400'}>{level}</Badge>;
  };

  const getSourceBadge = (source) => {
    const colors = {
      System: 'bg-purple-500/20 text-purple-400',
      SIP: 'bg-green-500/20 text-green-400',
      Extension: 'bg-blue-500/20 text-blue-400',
      Trunk: 'bg-cyan-500/20 text-cyan-400',
      Auth: 'bg-orange-500/20 text-orange-400',
      Security: 'bg-red-500/20 text-red-400'
    };
    return <Badge variant="outline" className={`border-0 ${colors[source] || 'bg-gray-500/20 text-gray-400'}`}>{source}</Badge>;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const uniqueSources = ['System', 'SIP', 'Extension', 'Trunk', 'Auth', 'Security'];

  if (loading && eventLogs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Event Log</h1>
          <p className="text-gray-400 mt-1">System events and activity logs</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="border-[#2a2a3d] text-gray-400 hover:text-white hover:bg-[#1e1e2d]">
            <Download className="w-4 h-4 mr-2" />
            Export Logs
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Total Events</p>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
              </div>
              <div className="p-3 bg-blue-500/20 rounded-lg">
                <ScrollText className="w-5 h-5 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Info</p>
                <p className="text-2xl font-bold text-blue-400">{stats.info}</p>
              </div>
              <div className="p-3 bg-blue-500/20 rounded-lg">
                <Info className="w-5 h-5 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Warnings</p>
                <p className="text-2xl font-bold text-yellow-400">{stats.warning}</p>
              </div>
              <div className="p-3 bg-yellow-500/20 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Errors</p>
                <p className="text-2xl font-bold text-red-400">{stats.error}</p>
              </div>
              <div className="p-3 bg-red-500/20 rounded-lg">
                <XCircle className="w-5 h-5 text-red-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-[#12121a] border-[#1e1e2d]">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                placeholder="Search events..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#1e1e2d] border-[#2a2a3d] text-white placeholder-gray-500"
              />
            </div>
            <Select value={levelFilter} onValueChange={setLevelFilter}>
              <SelectTrigger className="w-[150px] bg-[#1e1e2d] border-[#2a2a3d] text-white">
                <SelectValue placeholder="Level" />
              </SelectTrigger>
              <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="error">Error</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-[150px] bg-[#1e1e2d] border-[#2a2a3d] text-white">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                <SelectItem value="all">All Sources</SelectItem>
                {uniqueSources.map((source) => (
                  <SelectItem key={source} value={source}>{source}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="ghost" className="text-gray-400 hover:text-white" onClick={fetchData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Event Logs Table */}
      <Card className="bg-[#12121a] border-[#1e1e2d]">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#1e1e2d]">
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Timestamp</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Level</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Source</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Message</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Details</th>
                </tr>
              </thead>
              <tbody>
                {eventLogs.map((log) => (
                  <tr
                    key={log.id}
                    className="border-b border-[#1e1e2d] hover:bg-[#1e1e2d]/50 transition-colors"
                  >
                    <td className="p-4">
                      <span className="text-white font-mono text-sm">{formatTimestamp(log.timestamp)}</span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {getLevelIcon(log.level)}
                        {getLevelBadge(log.level)}
                      </div>
                    </td>
                    <td className="p-4">
                      {getSourceBadge(log.source)}
                    </td>
                    <td className="p-4">
                      <span className="text-white">{log.message}</span>
                    </td>
                    <td className="p-4">
                      <span className="text-gray-400 text-sm">{log.details}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-[#1e1e2d]">
            <p className="text-sm text-gray-400">
              Page {currentPage} of {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="border-[#2a2a3d] text-gray-400 hover:text-white disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-white px-3">{currentPage} / {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="border-[#2a2a3d] text-gray-400 hover:text-white disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {eventLogs.length === 0 && (
            <div className="p-12 text-center">
              <ScrollText className="w-12 h-12 mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400">No events found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EventLog;
